'use client';

import React from 'react';
import { InfoIcon } from './Tooltip';

interface MetricCardProps {
  label: string;
  value: string | number;
  description?: string;
  icon?: React.ReactNode;
  trend?: 'up' | 'down' | 'neutral';
  color?: 'blue' | 'green' | 'orange' | 'red' | 'purple';
  tooltip?: string;
}

export function MetricCard({
  label,
  value,
  description,
  icon,
  trend,
  color = 'blue',
  tooltip
}: MetricCardProps) {
  const colorClasses = {
    blue: 'bg-blue-50 border-blue-200 text-blue-600',
    green: 'bg-green-50 border-green-200 text-green-600',
    orange: 'bg-orange-50 border-orange-200 text-orange-600',
    red: 'bg-red-50 border-red-200 text-red-600',
    purple: 'bg-purple-50 border-purple-200 text-purple-600'
  };

  const trendIcons = {
    up: '↗',
    down: '↘',
    neutral: '→'
  };

  return (
    <div className={`rounded-lg border-2 p-6 ${colorClasses[color]}`}>
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="text-sm font-semibold text-gray-700">{label}</div>
          {tooltip && <InfoIcon tooltip={tooltip} />}
        </div>
        {icon && <div className="text-2xl">{icon}</div>}
      </div>
      <div className="text-3xl font-bold mb-1">{value}</div>
      {description && <div className="text-sm text-gray-600">{description}</div>}
      {trend && (
        <div className="mt-2 text-xs font-semibold flex items-center gap-1">
          <span>{trendIcons[trend]}</span>
          <span className="capitalize">{trend}</span>
        </div>
      )}
    </div>
  );
}

interface BreakdownTableProps {
  title: string;
  items: { label: string; value: string | number; percentage?: number; tooltip?: string }[];
  total?: { label: string; value: string | number };
}

export function BreakdownTable({ title, items, total }: BreakdownTableProps) {
  return (
    <div className="bg-white rounded-lg border-2 border-gray-200 overflow-hidden">
      <div className="bg-gray-50 px-6 py-4 border-b-2 border-gray-200">
        <h3 className="font-bold text-gray-900">{title}</h3>
      </div>
      <div className="divide-y divide-gray-200">
        {items.map((item, index) => (
          <div key={index} className="px-6 py-3 flex items-center justify-between hover:bg-gray-50">
            <div className="flex items-center gap-2">
              <span className="text-gray-700">{item.label}</span>
              {item.tooltip && <InfoIcon tooltip={item.tooltip} />}
              {item.percentage !== undefined && (
                <span className="text-xs text-gray-500">({item.percentage}%)</span>
              )}
            </div>
            <span className="font-semibold text-gray-900">{item.value}</span>
          </div>
        ))}
        {total && (
          <div className="px-6 py-4 flex items-center justify-between bg-blue-50 font-bold">
            <span className="text-gray-900">{total.label}</span>
            <span className="text-blue-600 text-lg">{total.value}</span>
          </div>
        )}
      </div>
    </div>
  );
}

interface ProgressBarProps {
  label: string;
  value: number;
  max: number;
  color?: 'blue' | 'green' | 'orange' | 'red';
  showPercentage?: boolean;
}

export function ProgressBar({
  label,
  value,
  max,
  color = 'blue',
  showPercentage = true
}: ProgressBarProps) {
  const percentage = Math.min((value / max) * 100, 100);

  const colorClasses = {
    blue: 'bg-blue-500',
    green: 'bg-green-500',
    orange: 'bg-orange-500',
    red: 'bg-red-500'
  };

  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-semibold text-gray-700">{label}</span>
        {showPercentage && (
          <span className="text-sm font-bold text-gray-900">{percentage.toFixed(0)}%</span>
        )}
      </div>
      <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
        <div
          className={`h-full ${colorClasses[color]} transition-all duration-500 rounded-full`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}

interface ComparisonCardProps {
  title: string;
  before: { label: string; value: string | number };
  after: { label: string; value: string | number };
  savings?: { label: string; value: string | number };
}

export function ComparisonCard({ title, before, after, savings }: ComparisonCardProps) {
  return (
    <div className="bg-white rounded-lg border-2 border-gray-200 p-6">
      <h3 className="font-bold text-gray-900 mb-4">{title}</h3>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">{before.label}</span>
          <span className="text-lg font-semibold text-red-600">{before.value}</span>
        </div>
        <div className="flex items-center justify-center">
          <div className="text-2xl text-gray-400">→</div>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">{after.label}</span>
          <span className="text-lg font-semibold text-green-600">{after.value}</span>
        </div>
        {savings && (
          <>
            <div className="border-t-2 border-gray-200 pt-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold text-gray-900">{savings.label}</span>
                <span className="text-xl font-bold text-blue-600">{savings.value}</span>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

interface RecommendationBadgeProps {
  recommendation: 'APPROVE' | 'CONSIDER' | 'DEFER' | 'REJECT';
}

export function RecommendationBadge({ recommendation }: RecommendationBadgeProps) {
  const badges = {
    APPROVE: {
      color: 'bg-green-100 text-green-800 border-green-300',
      icon: '✓',
      text: 'Recommended'
    },
    CONSIDER: {
      color: 'bg-blue-100 text-blue-800 border-blue-300',
      icon: '→',
      text: 'Worth Considering'
    },
    DEFER: {
      color: 'bg-orange-100 text-orange-800 border-orange-300',
      icon: '⏸',
      text: 'Consider Deferring'
    },
    REJECT: {
      color: 'bg-red-100 text-red-800 border-red-300',
      icon: '✗',
      text: 'Not Recommended'
    }
  };

  const badge = badges[recommendation];

  return (
    <div
      className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 font-bold text-sm ${badge.color}`}
    >
      <span className="text-lg">{badge.icon}</span>
      <span>{badge.text}</span>
    </div>
  );
}
